import { isArray, union } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectAPI } from 'api/project';
import { Task, TaskAPI, TaskType } from 'api/tasks';
import { TimesheetEntryType } from 'api/timesheets';
import { EnabledSourcesConfiguration } from 'api/timesheets/configuration/configuration';
import { ProjectInUserProfile } from 'api/user';
import { VirtualizedMenuList } from 'components/atoms/creatable-virtualised-list';
import { CreatableSelect } from 'components/atoms/select';
import { useCurrentUserProfile } from 'data/current-user-profile/hooks/useCurrentUserProfile';
import { CurrentUserProfileSliceState } from 'data/current-user-profile/slice';
import {
  ClickEventTags,
  LogType,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { useTaskListItems } from 'hooks/use-list-items';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  useGetAllActivitiesInProject,
  usePredefinedNonProjectActivities,
  usePredefinedProjectActivities,
} from 'queries/timesheets/activities';
import { logger } from 'services';
import { EntityOption } from './common/entity-option';

export const SelectContainer = styled.div`
  min-height: 350px;
  width: 272px;
  display: flex;
  flex-direction: column;
`;

export const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 'var(--spacing-05)',
  }),
  menu: () => ({ boxShadow: 'none' }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
};

const undefinedPromise = new Promise<undefined>((resolve) =>
  resolve(undefined),
);

export const useGetWorkOptions = ({
  timesheetEntriesConfig,
  project,
  predefinedActivitiesEnabled,
  apiCondition,
}: {
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  project?: ProjectInUserProfile;
  predefinedActivitiesEnabled: boolean;
  apiCondition?: boolean;
}) => {
  const {
    user: { userId },
  } = useUser();
  const { data: _allAdhocActivities } = useGetAllActivitiesInProject(userId);
  const { data: projectActivities } = usePredefinedProjectActivities();
  const { data: nonProjectActivities } = usePredefinedNonProjectActivities();
  const { data: currentUserProfile } = useCurrentUserProfile();
  const filterTaskListItem = useTaskListItems<Task>(
    currentUserProfile as unknown as CurrentUserProfileSliceState,
  );

  const [optionsState, setOptionsState] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const entityOptions: any[] = [];

    const apiCallCondition = apiCondition ?? true;

    (async () => {
      try {
        if (
          apiCallCondition &&
          (timesheetEntriesConfig?.TASK ||
            timesheetEntriesConfig?.MILESTONE ||
            timesheetEntriesConfig?.PHASE) &&
          project?.projectId != null
        ) {
          const [tasksResponse, phasesResponse] = await Promise.all([
            timesheetEntriesConfig?.TASK || timesheetEntriesConfig?.MILESTONE
              ? TaskAPI.getTasksAndMilestonesForProjects(
                  Number(project.projectId),
                )
              : undefinedPromise,
            timesheetEntriesConfig?.PHASE
              ? ProjectAPI.getPhasesForAProject(Number(project.projectId))
              : undefinedPromise,
          ]);

          const taskList: Task[] = [];
          const milestoneList: Task[] = [];

          if (tasksResponse != null) {
            if (isArray(tasksResponse)) {
              tasksResponse.forEach((element) => {
                if (element.type === TaskType.Milestone) {
                  milestoneList.push(element);
                } else {
                  taskList.push(element);
                }
              });
            }

            const myTaskList = filterTaskListItem(taskList, true);
            const otherTaskList = filterTaskListItem(taskList, false);
            const myMilestonesList = filterTaskListItem(milestoneList, true);
            const otherMilestonesList = filterTaskListItem(
              milestoneList,
              false,
            );

            const hasMyTasks = myTaskList && myTaskList.length > 0;
            const hasOtherTasks =
              otherTaskList && (otherTaskList ?? []).length > 0;
            const hasMyMilestones =
              timesheetEntriesConfig.MILESTONE &&
              myMilestonesList &&
              myMilestonesList.length > 0;
            const hasOtherMilestones =
              timesheetEntriesConfig.MILESTONE &&
              otherMilestonesList &&
              otherMilestonesList.length > 0;

            if (timesheetEntriesConfig?.TASK) {
              if ((taskList ?? [])?.length > 0) {
                const projectMyTaskOptions = {
                  label: timesheetEntriesConfig?.MILESTONE ? (
                    <FormattedMessage id="timesheets.title.my_tasks_milestones" />
                  ) : (
                    <FormattedMessage id="timesheets.title.my_tasks" />
                  ),
                  options: [
                    ...(hasMyTasks
                      ? myTaskList.map((t) => ({
                          label: t.taskName,
                          value: t.taskId,
                          type: TimesheetEntryType.TaskEntry,
                          extra: { ...t },
                        }))
                      : []),
                    ...(hasMyMilestones
                      ? myMilestonesList.map((m) => ({
                          label: m.taskName,
                          value: m.taskId,
                          type: TimesheetEntryType.TaskEntry,
                          extra: { ...m },
                        }))
                      : []),
                  ],
                };

                const projectOtherTaskOptions = {
                  label:
                    projectMyTaskOptions &&
                    projectMyTaskOptions.options.length > 0 ? (
                      <FormattedMessage id="timesheets.title.other_tasks" />
                    ) : (
                      <FormattedMessage id="timesheets.title.tasks" />
                    ),
                  options: [
                    ...(hasOtherTasks
                      ? otherTaskList.map((t) => ({
                          label: t.taskName,
                          value: t.taskId,
                          type: TimesheetEntryType.TaskEntry,
                          extra: { ...t },
                        }))
                      : []),
                  ],
                };

                entityOptions.push(projectMyTaskOptions);
                entityOptions.push(projectOtherTaskOptions);
              }
            }
            if (timesheetEntriesConfig?.MILESTONE) {
              if ((milestoneList ?? [])?.length > 0) {
                const projectMilestoneOptions = {
                  label:
                    myMilestonesList.length > 0 ? (
                      <FormattedMessage id="timesheets.title.other_milestones" />
                    ) : (
                      <FormattedMessage id="timesheets.title.milestones" />
                    ),
                  options: [
                    ...(hasOtherMilestones
                      ? otherMilestonesList.map((t) => ({
                          label: t.taskName,
                          value: t.taskId,
                          type: TimesheetEntryType.TaskEntry,
                          extra: { ...t },
                        }))
                      : []),
                  ],
                };

                entityOptions.push(projectMilestoneOptions);
              }
            }
          }
          if (phasesResponse != null) {
            const projectPhaseOptions = phasesResponse.map((phase) => ({
              label: phase.projectPhaseName,
              value: phase.projectPhaseId,
              type: TimesheetEntryType.PhaseEntry,
              extra: {
                ...phase,
                project: {
                  id: project.projectId,
                  name: project.projectName,
                },
              },
            }));

            if ((projectPhaseOptions ?? [])?.length > 0) {
              const projectPhasesObject = {
                label: 'Phases',
                options: projectPhaseOptions,
              };

              entityOptions.push(projectPhasesObject);
            }
          }
        }
      } catch (error) {
        logger.error('SEARCH FAILED', { error });
      } finally {
        if (apiCallCondition && timesheetEntriesConfig?.ADHOC) {
          let allActivities: string[];
          const allAdhocActivities = predefinedActivitiesEnabled
            ? []
            : _allAdhocActivities;

          const projectActivityNames = projectActivities?.map(
            (x) => x.activityName,
          );

          const nonProjectActivityNames = nonProjectActivities?.map(
            (x) => x.activityName,
          );

          if (project) {
            allActivities = union(projectActivityNames, allAdhocActivities);
          } else {
            allActivities = union(nonProjectActivityNames, allAdhocActivities);
          }

          if ((allActivities ?? [])?.length > 0) {
            const projectActivityOptions = {
              label: 'Activities',
              options: allActivities?.map((activityName) => ({
                label: activityName,
                value: activityName,
                type: TimesheetEntryType.AdhocEntry,
              })),
            };

            entityOptions.push(projectActivityOptions);
          }
        }
        setOptionsState(entityOptions);
        setIsLoading(false);
      }
    })();
  }, [
    _allAdhocActivities,
    apiCondition,
    filterTaskListItem,
    nonProjectActivities,
    predefinedActivitiesEnabled,
    project,
    projectActivities,
    timesheetEntriesConfig?.ADHOC,
    timesheetEntriesConfig?.MILESTONE,
    timesheetEntriesConfig?.PHASE,
    timesheetEntriesConfig?.TASK,
  ]);

  return { optionsState, isLoading };
};

export const TimesheetWorkOptionsSelect = ({
  onCreateOption,
  onSelection,
  handleVisibilityToggle,
  timesheetEntriesConfig,
  predefinedActivitiesEnabled,
  value,
  optionsState,
  isLoading,
}: {
  onCreateOption: (props) => void;
  onSelection: (props) => void;
  handleVisibilityToggle?: (visibility: boolean) => void;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  predefinedActivitiesEnabled: boolean;
  value?: number;
  optionsState: any;
  isLoading: boolean;
}) => {
  const [searchedText, setSearchedText] = useState<string>('');

  const { executeLogger } = useLoadLoggerForDataDog(
    [ClickEventTags.TimesheetTaskPopupOpen],
    LogType.CallbackLog,
  );

  const commonProps = {
    menuIsOpen: true,
    autoFocus: true,
    components: {
      ClearIndicator: null,
      IndicatorSeparator: null,
      Option: EntityOption,
      MenuList: VirtualizedMenuList,
      LoadingIndicator: null,
    },
    tabSelectsValue: false,
    styles: selectStyles,
    grouped: true,
    options: optionsState,
    onChange: onSelection,
    onBlur: () => handleVisibilityToggle?.(false),
    isValidNewOption: () =>
      !!timesheetEntriesConfig?.ADHOC &&
      searchedText !== '' &&
      !predefinedActivitiesEnabled,
    searchedText,
    onInputChange: (input) => setSearchedText(input),
    isLoading,
    value,
  };

  const translate = useTranslate();

  return (
    <SelectContainer data-cy="timesheet-work-select">
      <CreatableSelect
        {...commonProps}
        placeholder={<FormattedMessage id="placeholder.search" />}
        formatCreateLabel={(inputValue: string) =>
          translate('timesheets.adhoc.create_activity', {
            activityName: inputValue,
          })
        }
        onCreateOption={onCreateOption}
        noOptionsMessage={() => {
          executeLogger();

          return <FormattedMessage id="no.options" />;
        }}
      />
    </SelectContainer>
  );
};
